import { Checkbox, FormControlLabel } from '@mui/material';
import { FormCaptureInput } from 'components/common/FormCaptureInput';
import { FormDropdown } from 'components/common/FormDropdown';
import { useNavigate, useParams } from 'react-router-dom';
import { TaskPicker } from 'components/common/TaskPicker';
import FormInput from 'components/FormInput';
import moment, { Moment } from 'moment';
import { Button, Form, FormLabel } from 'react-bootstrap';
import { useFormik } from 'formik';
import { addIncidentSchema } from './schema';
import { AddIncidentProps } from './types';
import toast from 'react-hot-toast';
import Modal from 'components/common/Modal/Modal';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootStates } from 'redux/store';
import React, { useEffect, useState } from 'react';
import { IIncidents } from 'redux/services/incident';
import { AddModal } from 'components/common/AddModal';
import './AddIncident.scss';
import { DateTimePicker } from 'components/common/DateTimePicker';

export const AddIncident: React.FC<AddIncidentProps> = ({
  incident,
  disabled,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { projectId, incidentId } = useParams();
  const [otherValue, setOtherValue] = useState('Other');
  const [show, setShowOtherModal] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const incidentTypes = useSelector(
    (state: RootStates) => state.auth.userDetails.incidentTypes ?? [],
  );
  const severityItems = useSelector(
    (state: RootStates) =>
      state.auth.userDetails.severity?.map((item) => ({ name: item })) ?? [],
  );

  const occuranceRates = useSelector(
    (state: RootStates) =>
      state.auth.userDetails.occuranceRates?.map((item) => ({ name: item })) ??
      [],
  );

  const formik = useFormik<Partial<IIncidents>>({
    initialValues: {
      incidentType: '',
      severity: '',
      occuranceRate: '',
      description: '',
      images: [],
      locations: [],
      rcaRequired: true,
      rcaDate: '',
    },
    validationSchema: addIncidentSchema,
    onSubmit: () => setShowConfirmation(true),
  });

  useEffect(() => {
    if (incident?.incidentType && !formik.values.incidentType) {
      formik.setValues({
        ...incident,
        images: incident.images ?? [],
      });
    }
  }, [formik, incident]);

  const handleAddNewLocationItem = (item: string) => {
    if (!formik.values.locations?.includes(item)) {
      const newLocationItems = [...(formik.values.locations ?? []), item];

      formik.setFieldValue('locations', newLocationItems);
    } else {
      toast.error(t('error_location_already_exists'));
    }
  };

  const handleRemoveLocationItem = (locationItems: string[]) => {
    formik.setFieldValue('locations', locationItems);
  };

  const handleCaptureImage = (image: string) => {
    const images = [
      ...(formik.values.images ?? []),
      { docType: 'image', url: image },
    ];

    formik.setFieldValue('images', images);
  };

  const handleDeleteImage = (images: string[]) =>
    formik.setFieldValue(
      'images',
      images.map((img) => ({ docType: 'image', url: img })),
    );

  const handleDateChange = (value: Moment) => {
    formik.setFieldValue('rcaDate', value.toISOString());
  };

  const handleSelectOccuranceRate = (value: { name: string }) =>
    formik.setFieldValue('occuranceRate', value.name);

  const handleSelectSeverity = (value: { name: string }) =>
    formik.setFieldValue('severity', value.name);

  const handleRCAChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    formik.setFieldValue('rcaRequired', !e.target.checked);

  const handleBack = () =>
    navigate(`/project/${projectId}/record-incident/${incidentId}/0`);

  const handleActionClick = () => setShowOtherModal(true);
  const handleCloseModal = () => setShowOtherModal(false);
  const handleSubmitModal = (item: string) => {
    setShowOtherModal(false);
    setOtherValue(item);
    formik.setFieldValue('incidentType', item);
  };

  const handleSubmit = async () => {
    setShowConfirmation(false);
    onSubmit({
      projectId,
      incidentId,
      body: formik.values,
    });
  };

  const handleClose = () => setShowConfirmation(false);

  return (
    <form
      className="d-flex flex-column add-incident h-100"
      onSubmit={formik.handleSubmit}
    >
      <div className="d-flex flex-column flex-grow-1 overflow-auto">
        <div className="d-flex flex-column mx-2">
          <FormLabel>
            {t('label_incident_type')} <span>*</span>
          </FormLabel>
          <div className="btn-container">
            {incidentTypes.map((val) => (
              <Button
                name="incidentType"
                key={val}
                value={val}
                disabled={disabled}
                className={`btn ${
                  formik.values.incidentType === val ? 'active' : ''
                }`}
                onClick={formik.handleChange}
              >
                {val}
              </Button>
            ))}
            <Button
              name="incidentType"
              value={formik.values.incidentType}
              disabled={disabled}
              className={`btn ${
                formik.values.incidentType === otherValue ? 'active' : ''
              }`}
              onClick={handleActionClick}
            >
              {otherValue}
            </Button>
          </div>
          {formik.errors.incidentType && formik.touched.incidentType && (
            <span className="error-text">
              *{t('error_specify_incident_type')}
            </span>
          )}
        </div>
        <div className="d-flex flex-column flex-md-row">
          <FormInput
            label={t('label_description')}
            type="text"
            value={formik.values.description}
            name="description"
            disabled={disabled}
            as="textarea"
            touched={formik.touched.description}
            error={formik.errors.description}
            onChange={formik.handleChange}
          />
          <FormCaptureInput
            label={t('label_add_photo')}
            images={formik.values.images?.map((img) => img.url)}
            limit={3}
            required={false}
            path={`projects/${projectId}/incidents/${incidentId}/${
              (formik.values.images?.length ?? 0) + 1
            }`}
            disabled={disabled}
            onCapture={handleCaptureImage}
            onDelete={handleDeleteImage}
          />
        </div>
        <div className="d-flex flex-column flex-md-row">
          <TaskPicker
            items={formik.values.locations}
            required
            error={formik.touched.locations ? formik.errors.locations : ''}
            disabled={disabled}
            additionalInfo={t('hashtag_additional_info_incident')}
            onAddNewItem={handleAddNewLocationItem}
            onRemoveItem={handleRemoveLocationItem}
          />
          <FormDropdown<{ name: string }>
            required
            items={severityItems}
            selectedItem={formik.values.severity}
            disabled={disabled}
            lable={t('label_severity')}
            error={formik.touched.severity ? formik.errors.severity : ''}
            onSelect={handleSelectSeverity}
          />
        </div>
        <div className="d-flex flex-column flex-md-row">
          <FormDropdown<{ name: string }>
            required
            items={occuranceRates}
            disabled={disabled}
            selectedItem={formik.values.occuranceRate}
            lable={t('label_occurance_rate')}
            error={
              formik.touched.occuranceRate ? formik.errors.occuranceRate : ''
            }
            onSelect={handleSelectOccuranceRate}
          />
          <span className="empty-placeholder" />
        </div>

        <div className="d-flex flex-column root-cause-container">
          <FormLabel>
            {t('label_root_cause_analysis')} <span>*</span>
          </FormLabel>
          <div className="d-flex flex-column">
            <div
              className="d-flex align-items-start align-items-md-center
 flex-column flex-md-row"
            >
              <FormControlLabel
                className="checkbox-not-required"
                control={
                  <Checkbox
                    name="rootCauseAnalysisNotRequired"
                    color="primary"
                    disabled={disabled}
                    checked={!formik.values.rcaRequired}
                    onChange={handleRCAChange}
                  />
                }
                label={t('label_not_required')}
              />
              <span className="d-block mr-1 or-placeholder">OR</span>
              <DateTimePicker
                value={moment(formik.values.rcaDate).toDate()}
                label={t('label_pick_date')}
                disabled={!formik.values.rcaRequired || disabled}
                onChange={handleDateChange}
              />
            </div>
            {formik.errors.rcaDate && formik.touched.rcaDate && (
              <Form.Control.Feedback type="invalid" className="d-block">
                {formik.errors.rcaDate}
              </Form.Control.Feedback>
            )}
          </div>
        </div>
      </div>
      {!disabled && (
        <div className="d-flex justify-content-between footer-content">
          <Button onClick={handleBack}>{t('btn_back')}</Button>
          <Button type="submit">{t('submit')}</Button>
        </div>
      )}

      <AddModal
        open={show}
        title={t('modal_title_other_value')}
        inputLabel={t('label_specify_other_value')}
        onSubmit={handleSubmitModal}
        onClose={handleCloseModal}
      />
      <Modal
        show={showConfirmation}
        withIcon
        type={'success'}
        title=""
        modalClassName="checklist-modal"
        submitBtnText={t('submit')}
        cancelBtnText={t('btn_goback')}
        onSubmit={handleSubmit}
        onClose={handleClose}
      />
    </form>
  );
};
